// The requests changelist sends, as a failure's Details names them: every
// request carries an `X-Request-Id` the client chose, which the server logs
// and echoes, so a failure can be found in the server's log even when no
// response came back.

/** A request that was sent, and what came of it. */
export interface RequestRecord {
  method: string;
  /** The path, without the query string. */
  path: string;
  /** The ID the server answered with, else the one the client sent. */
  requestId: string;
  /** When it was sent (epoch ms). */
  at: number;
  /** The response's HTTP status; null when no response came back. */
  status: number | null;
}

export const REQUEST_ID_HEADER = 'X-Request-Id';

export function newRequestId(): string {
  return crypto.randomUUID();
}

const records = new WeakMap<Response, RequestRecord>();

/** Remembers which request `res` answers (see `requestOf`). */
export function noteResponse(res: Response, sent: Omit<RequestRecord, 'status'>): void {
  records.set(res, {
    ...sent,
    requestId: res.headers.get(REQUEST_ID_HEADER) || sent.requestId,
    status: res.status,
  });
}

/** The request `res` answers, when it came through the changelist transport. */
export function requestOf(res: Response): RequestRecord | null {
  return records.get(res) ?? null;
}

/** A request that got no response: the network failed, or nothing answered. */
export class NoResponseError extends Error {
  constructor(
    public request: RequestRecord,
    cause: unknown,
  ) {
    super(`No response from Changeling's server (${cause instanceof Error ? cause.message : String(cause)})`, { cause });
  }
}
